import React from 'react';
import { Modal } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRotateLeft, faCircleCheck, faHeart, faMoneyBillTransfer } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button } from '../../../../../components/Button';
import { formatMoney } from '../../../../../utils/money';
import classes from './cash-change-dialog.module.scss';

type CashChangeDialogProps = {
  isOpen: boolean;
  tenderedAmount: number;
  dueAmount: number;
  isSubmitting: boolean;
  onReturnChange: () => void;
  onAddToTip: () => void;
  onCancel: () => void;
};

export const CashChangeDialog: React.FC<CashChangeDialogProps> = ({
  isOpen,
  tenderedAmount,
  dueAmount,
  isSubmitting,
  onReturnChange,
  onAddToTip,
  onCancel,
}) => {
  const changeAmount = Math.round((tenderedAmount - dueAmount) * 10) / 10;

  return (
    <Modal
      opened={isOpen}
      onClose={onCancel}
      centered
      withCloseButton={false}
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
      classNames={{ content: classes.content, body: classes.body }}
    >
      <div className={classes.dialog} dir="rtl" data-testid="cash-change-dialog">
        <div className={classes.success}>
          <FontAwesomeIcon icon={faCircleCheck as IconProp} />
          <span>שולם בהצלחה!</span>
        </div>
        <span className={classes.eyebrow}>עודף ללקוח</span>
        <strong className={classes.changeAmount} data-testid="cash-change-amount">{formatMoney(changeAmount)} <span>₪</span></strong>
        <span className={classes.summary}>הלקוח שילם ₪ {formatMoney(tenderedAmount)} עבור חשבון של ₪ {formatMoney(dueAmount)}</span>

        <div className={classes.amountGrid}>
          <div className={classes.amountCard}>
            <span>עודף</span>
            <strong>{formatMoney(changeAmount)} ₪</strong>
          </div>
          <div className={classes.amountCard}>
            <span>חשבון</span>
            <strong>{formatMoney(dueAmount)} ₪</strong>
          </div>
          <div className={classes.amountCard}>
            <span>שולם</span>
            <strong>{formatMoney(tenderedAmount)} ₪</strong>
          </div>
        </div>

        <h2 className={classes.question}>מה לעשות עם העודף?</h2>
        <div className={classes.actions}>
          <Button variant="confirm" fullWidth size="lg" dataTestId="cash-change-add-to-tip" className={`${classes.actionRow} ${classes.tipAction}`} onClick={onAddToTip} disabled={isSubmitting}>
            <span className={classes.actionIcon}><FontAwesomeIcon icon={faHeart as IconProp} /></span>
            <span className={classes.actionCopy}>
              <strong>הפוך לטיפ <em>מומלץ</em></strong>
              <small>₪ {formatMoney(changeAmount)} יתווספו כטיפ למלצר</small>
            </span>
          </Button>
          <Button variant="save" fullWidth size="lg" dataTestId="cash-change-return" className={`${classes.actionRow} ${classes.returnAction}`} onClick={onReturnChange} disabled={isSubmitting}>
            <span className={classes.actionIcon}><FontAwesomeIcon icon={faMoneyBillTransfer as IconProp} /></span>
            <span className={classes.actionCopy}>
              <strong>החזר עודף ללקוח</strong>
              <small>החזר ₪ {formatMoney(changeAmount)} במזומן ואשר</small>
            </span>
          </Button>
          <Button variant="cancel" fullWidth size="lg" dataTestId="cash-change-cancel" className={`${classes.actionRow} ${classes.cancelAction}`} onClick={onCancel} disabled={isSubmitting}>
            <span className={classes.actionIcon}><FontAwesomeIcon icon={faArrowRotateLeft as IconProp} /></span>
            <span className={classes.actionCopy}>
              <strong>ביטול - חזור לתשלום</strong>
              <small>בטל את התשלום ותקן את הסכום</small>
            </span>
          </Button>
        </div>
      </div>
    </Modal>
  );
};
